/* eslint-disable no-underscore-dangle */
import React, { useContext, useEffect, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { Stack, Typography, Icon } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  QuickMessagesContainer,
  List,
  ListItem,
  ShortcutMessage,
  ListItemHeader,
  ListItemBody,
  ListItemAdd,
} from 'views/home/Chat/QuickMessages/style';

import CannedMessagesContext from 'contexts/cannedMessages/context';
import InputSearch from 'components/InputSearch';
import { useChatStore } from 'redux/reducers/chatReducer';
import { useAppSelector } from 'hooks/redux';
import { selectChatSelectedCannedMessages } from 'redux/selectors';
import TagList from 'components/Tags/TagList';

const QuickMessages: React.FC<{ isTabsMode?: boolean }> = ({ isTabsMode = false }) => {
  const { t } = useTranslation();
  const [inputText, setInputText] = useState('');

  const {
    filteredCannedMessages,
    handleCannedMessageSelected,
    handleMessageOnHover,
    cannedMessagesReferences,
    cursor,
    handleCursor,
    filterCannedMessages,
    handleMessageSelection,
    serviceSelected,
  } = useContext(CannedMessagesContext);
  const selectedCannedMessages = useAppSelector(selectChatSelectedCannedMessages);
  const { setSelectedCannedMessages } = useChatStore();

  const keyHandler = React.useCallback(
    ({ key }: KeyboardEvent) => {
      if (key === 'ArrowDown') {
        const finalCursor = cursor !== filteredCannedMessages.length - 1 ? cursor + 1 : 0;

        handleMessageOnHover(filteredCannedMessages[finalCursor].text);
        handleCursor(finalCursor);
      } else if (key === 'ArrowUp') {
        const finalCursor = cursor !== 0 ? cursor - 1 : filteredCannedMessages.length - 1;

        handleMessageOnHover(filteredCannedMessages[finalCursor].text);
        handleCursor(finalCursor);
      } else if (key === 'Enter') {
        handleMessageSelection(true);
      } else if (key === 'Tab') {
        handleMessageSelection(true);
      }
    },
    [cursor, filteredCannedMessages, handleCursor, handleMessageOnHover, handleMessageSelection]
  );

  useEffect(() => {
    window.addEventListener('keyup', keyHandler);
    return () => {
      window.removeEventListener('keyup', keyHandler);
    };
  }, [cursor, keyHandler]);

  return (
    <QuickMessagesContainer data-testid="quickMessages" isTabsMode={isTabsMode}>
      {!isTabsMode && (
        <Typography variant="subtitle2" margin="1.2rem" marginBottom="0.4rem" align="center">
          {t('quickMessages')}
        </Typography>
      )}
      <InputSearch
        placeholder="search"
        value={inputText}
        onChange={(value) => {
          filterCannedMessages(value);
          setInputText(value);
        }}
      />

      <List>
        {filteredCannedMessages.length ? (
          filteredCannedMessages.map((quickMsg, idx: number) => (
            <ListItem
              ref={cannedMessagesReferences[idx]}
              tabIndex={0}
              key={uuidv4()}
              onClick={() => {
                handleCannedMessageSelected(quickMsg.text, true);
                setSelectedCannedMessages({
                  ...selectedCannedMessages,
                  [serviceSelected._id]: quickMsg,
                });
              }}
            >
              <ListItemBody>
                <ListItemHeader>
                  <Typography id="shortcut-id" variant="body2">
                    {quickMsg.shortcut}
                  </Typography>
                  <TagList tags={quickMsg.customTags?.map(({ tag }) => tag) || []} maxTags={2} />
                </ListItemHeader>
                <ShortcutMessage variant="body1">{quickMsg.text}</ShortcutMessage>
              </ListItemBody>
              <ListItemAdd>
                <Icon className="fa-regular fa-square-plus" color="inherit" />
              </ListItemAdd>
            </ListItem>
          ))
        ) : (
          <Stack sx={{ padding: '26px 16px' }}>
            <Typography variant="body2" color="neutral.var90">
              {t('withoutResults')}
            </Typography>
            <Typography variant="body1" color="neutral.var70">
              {t('withoutResultsMessage')}
            </Typography>
          </Stack>
        )}
      </List>
    </QuickMessagesContainer>
  );
};

export default QuickMessages;
